// Enums
import { PostMessageOutgoingAction } from '@/enums/PostMessageAction';

// Redux
import { Action, Result } from '../index';

// Types
import { PostMessageRetry, PostMessageSetRetryParams, PostMessageState } from './types/post-message';

// Initial state
const retryDefaultValues: PostMessageRetry = {
  context: {},
  delay: 1000,
  executionCount: 0,
  executionLimit: 5,
};

const INITIAL_STATE: PostMessageState = {
  retry: {
    getContactsAndPortfolios: retryDefaultValues,
    getPortfolios: retryDefaultValues,
  },
  // household and contacts to use below:
  isPostMessageActive: false,
  postMessageActivation: {
    contact: false,
    portfolio: false,
  },
  // both above new values get updated on launch
};

// Actions
export const RESET_RETRY_GET_BOOK_OF_BUSINESS: Action = {
  type: 'connect/post-message/RESET_RETRY_GET_BOOK_OF_BUSINESS',
};

export const RESET_RETRY_GET_PORTFOLIOS: Action = {
  type: 'connect/post-message/RESET_RETRY_GET_PORTFOLIOS',
};

export const SET_IS_POST_MESSAGE_ACTIVE: Action = {
  type: 'connect/post-message/SET_IS_POST_MESSAGE_ACTIVE',
};

export const SET_IS_CONTACT_POST_MESSAGE_ACTIVE: Action = {
  type: 'connect/post-message/SET_IS_CONTACT_POST_MESSAGE_ACTIVE',
};

export const SET_IS_PORTFOLIO_POST_MESSAGE_ACTIVE: Action = {
  type: 'connect/post-message/SET_IS_PORTFOLIO_POST_MESSAGE_ACTIVE',
};

export const UPDATE_RETRY_GET_BOOK_OF_BUSINESS: Action = {
  type: 'connect/post-message/UPDATE_RETRY_GET_BOOK_OF_BUSINESS',
};

export const UPDATE_RETRY_GET_PORTFOLIO: Action = {
  type: 'connect/post-message/UPDATE_RETRY_GET_PORTFOLIO',
};

// Reducer
export default function reducer(state: PostMessageState = INITIAL_STATE, action: Action): PostMessageState {
  switch (action.type) {
    case `${RESET_RETRY_GET_BOOK_OF_BUSINESS.type}`: {
      return {
        ...state,
        retry: {
          ...state.retry,
          getContactsAndPortfolios: retryDefaultValues,
        },
      };
    }
    case `${RESET_RETRY_GET_PORTFOLIOS.type}`: {
      return {
        ...state,
        retry: {
          ...state.retry,
          getPortfolios: retryDefaultValues,
        },
      };
    }
    case `${SET_IS_POST_MESSAGE_ACTIVE.type}`: {
      return {
        ...state,
        isPostMessageActive: true,
        postMessageActivation: {
          contact: true,
          portfolio: true,
        },
      };
    }
    case `${SET_IS_CONTACT_POST_MESSAGE_ACTIVE.type}`: {
      return {
        ...state,
        postMessageActivation: {
          contact: action.isActive,
          portfolio: state.postMessageActivation.portfolio,
        },
      };
    }
    case `${SET_IS_PORTFOLIO_POST_MESSAGE_ACTIVE.type}`: {
      return {
        ...state,
        postMessageActivation: {
          contact: state.postMessageActivation.contact,
          portfolio: action.isActive,
        },
      };
    }
    case `${UPDATE_RETRY_GET_BOOK_OF_BUSINESS.type}`: {
      const { user } = action;
      return {
        ...state,
        retry: {
          ...state.retry,
          getContactsAndPortfolios: {
            ...state.retry.getContactsAndPortfolios,
            executionCount: state.retry.getContactsAndPortfolios.executionCount + 1,
            user,
          },
        },
      };
    }
    case `${UPDATE_RETRY_GET_PORTFOLIO.type}`: {
      const { user, portfolios } = action;
      return {
        ...state,
        retry: {
          ...state.retry,
          getPortfolios: {
            ...state.retry.getPortfolios,
            context: {
              portfolios,
            },
            executionCount: state.retry.getPortfolios.executionCount + 1,
            user,
          },
        },
      };
    }
    default: {
      return state;
    }
  }
}

// Action Creators
export const resetPostMessageRetry =
  (action: PostMessageOutgoingAction): Result<void> =>
  (dispatch, getState) => {
    switch (action) {
      case PostMessageOutgoingAction.FACTSET_GET_BOOK_OF_BUSINESS:
        dispatch({ type: RESET_RETRY_GET_BOOK_OF_BUSINESS.type });
        break;

      case PostMessageOutgoingAction.FACTSET_GET_PORTFOLIOS:
        dispatch({ type: RESET_RETRY_GET_PORTFOLIOS.type });
        break;
    }
  };

export const setIsActivePostMessage = (): Result<void> => dispatch => {
  dispatch({ type: SET_IS_POST_MESSAGE_ACTIVE.type });
};

export const setIsActiveContactPostMessage =
  (isActive: boolean): Result<void> =>
  dispatch => {
    dispatch({ type: SET_IS_CONTACT_POST_MESSAGE_ACTIVE.type, isActive });
  };

export const setIsActivePortfolioPostMessage =
  (isActive: boolean): Result<void> =>
  dispatch => {
    dispatch({ type: SET_IS_PORTFOLIO_POST_MESSAGE_ACTIVE.type, isActive });
  };

export const updatePostMessageRetry =
  (setRetryParams: PostMessageSetRetryParams): Result<void> =>
  (dispatch, getState) => {
    const { action, context } = setRetryParams;
    const { user } = getState().auth;

    switch (action) {
      case PostMessageOutgoingAction.FACTSET_GET_BOOK_OF_BUSINESS:
        dispatch({ type: UPDATE_RETRY_GET_BOOK_OF_BUSINESS.type, user });
        break;
      case PostMessageOutgoingAction.FACTSET_GET_PORTFOLIOS:
        dispatch({ type: UPDATE_RETRY_GET_PORTFOLIO.type, portfolios: context?.portfolios, user });
        break;
    }
  };
